import {
  getGlobalStats,
  getCompanyStats,
  getWeeklyStats,
  getMonthlyStats,
  getMonthlyDailyStats,
  getEmployeeHours,
} from "@/services/stats.service";
import { useQuery } from "@tanstack/react-query";

export const useGlobalStats = () => {
  return useQuery({
    queryKey: ["globalStats"],
    queryFn: () => getGlobalStats(),
  });
};

export const useCompanyStats = (companyId?: string, period?: string) => {
  // S'assurer que period a toujours une valeur par défaut
  const periodValue = period || "month";

  return useQuery({
    queryKey: ["companyStats", companyId, periodValue], // Utiliser periodValue au lieu de period
    queryFn: () => getCompanyStats(companyId!, periodValue), // Utiliser periodValue
    enabled: !!companyId,
    refetchOnMount: true, // Forcer le refetch quand le composant monte
    refetchOnWindowFocus: false, // Ne pas refetch quand la fenêtre reprend le focus
  });
};

// Hook pour récupérer les statistiques hebdomadaires (7 derniers jours)
export const useWeeklyStats = (companyId?: string) => {
  return useQuery({
    queryKey: ["weeklyStats", companyId],
    queryFn: () => getWeeklyStats(companyId),
  });
};

// Hook pour récupérer les statistiques mensuelles (6 derniers mois)
export const useMonthlyStats = (companyId?: string) => {
  return useQuery({
    queryKey: ["monthlyStats", companyId],
    queryFn: () => getMonthlyStats(companyId),
  });
};

// Hook pour récupérer les statistiques quotidiennes du mois en cours
export const useMonthlyDailyStats = (period?: string, companyId?: string) => {
  return useQuery({
    queryKey: ["monthlyDailyStats", period, companyId],
    queryFn: () => getMonthlyDailyStats(period, companyId),
  });
};

/**
 * Hook pour récupérer les heures travaillées par employé.
 * Se recharge automatiquement quand period ou refDate change.
 *
 * @param period   "day" | "week" | "month"
 * @param refDate  La date de référence choisie par l'utilisateur
 */
export const useEmployeeHours = (period: string, refDate: Date) => {
  // On utilise toDateString() dans la queryKey pour que React Query
  // détecte le changement de date même si l'objet Date change
  return useQuery({
    queryKey: ["employeeHours", period, refDate.toDateString()],
    queryFn: () => getEmployeeHours(period, refDate),
  });
};
